import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { CONNECT_API } from '../shared/app-token';
import { UserSettings } from '../types/user-settings';
import { User } from '../types/user';

@Injectable({
    providedIn: 'root'
})
export class UserProfileService {
    /** reference on connect.fat api endpoint in environment */
    #apiUrl = inject(CONNECT_API);

    #httpClient = inject(HttpClient);

    /**
     * get user profile details by id
     * @param {string} OID - unique id of user
     * @returns {object} - json object with user details
     */
    getUserProfile(OID: string) {
        return this.#httpClient.get<User>(`${this.#apiUrl}/users/${OID}/profile`);
    }

    /**
     * save user details
     * @param {string} userID - truncated user azure homeAccountId, see {@link AuthService#setAccount}
     * @param  dto   user profile language & format locale
     * example dto: {
     *      preferredLanguage: 'en-US',
     *      formatLocale: 'de-DE'
     * }
     * @returns {object} observable of api response
     */
    updateUserProfile(userID: string, dto: UserSettings) {
        return this.#httpClient.put<UserSettings>(`${this.#apiUrl}/users/${userID}/profile`, dto);
    }

    /**
     * get user's signature
     * @param {string} userID - unique id of user
     * @returns {object} observable of api response
     */
    getUserSignature(userID: string) {
        return this.#httpClient.get(`${this.#apiUrl}/users/${userID}/signature`, { responseType: 'blob' });
    }

    /**
     * delete user's signature
     * @param {string} userID - unique id of user
     * @returns {object} observable of api response
     */
    deleteUserSignature(userID: string) {
        return this.#httpClient.delete(`${this.#apiUrl}/users/${userID}/signature`);
    }

    /**
     * upload user signature
     * @param {string} userID - user id
     * @param {object} files - files to upload
     * @returns {object} observable of api response
     */
    uploadUserSignature(userID: string, files: File[]) {
        const headers = new HttpHeaders();
        headers.set('Content-Type', 'multipart/form-data');

        const formData = new FormData();
        // as a user has only one signature, we just pick the first file in list
        formData.append('image', files[0]);

        return this.#httpClient.put(`${this.#apiUrl}/users/${userID}/signature`, formData, {
            headers,
            reportProgress: true,
            observe: 'events'
        });
    }
}
